import Phaser from 'phaser';
import { COLORS, ChallengeType, ChallengeEvent } from '../utils/constants';

interface ActiveChallenge {
  data: ChallengeEvent;
  container: Phaser.GameObjects.Container;
  timer: Phaser.Time.TimerEvent;
  bg: Phaser.GameObjects.Rectangle;
  text: Phaser.GameObjects.Text;
  highlight?: Phaser.GameObjects.Text;
}

export class ChallengeSystem {
  private scene: Phaser.Scene;
  private activeChallenges: ActiveChallenge[] = [];
  private spawnTimer: Phaser.Time.TimerEvent | null = null;
  private spawnInterval = 5000;
  private maxActive = 2;
  private difficultyScale = 1;
  private challengeCount = 0;
  private successCount = 0;
  private failCount = 0;
  private graphics: Phaser.GameObjects.Graphics;

  // Challenge definitions
  private readonly colorChallenges = [
    { value: '红色', key: 'R', color: COLORS.red, displayColor: '#ff1744' },
    { value: '蓝色', key: 'B', color: COLORS.blue, displayColor: '#1a8fff' },
    { value: '绿色', key: 'G', color: COLORS.green, displayColor: '#00e676' },
    { value: '紫色', key: 'P', color: COLORS.purple, displayColor: '#9945ff' },
    { value: '青色', key: 'C', color: COLORS.cyan, displayColor: '#00f0ff' },
  ];

  private readonly arrowChallenges = [
    { value: '←', key: 'LEFT', displayColor: '#ffea00' },
    { value: '→', key: 'RIGHT', displayColor: '#ffea00' },
    { value: '↑', key: 'UP', displayColor: '#ffea00' },
    { value: '↓', key: 'DOWN', displayColor: '#ffea00' },
  ];

  private readonly numberChallenges = [
    { value: '1', key: 'ONE', displayColor: '#00f0ff' },
    { value: '2', key: 'TWO', displayColor: '#00f0ff' },
    { value: '3', key: 'THREE', displayColor: '#00f0ff' },
    { value: '4', key: 'FOUR', displayColor: '#00f0ff' },
    { value: '5', key: 'FIVE', displayColor: '#00f0ff' },
  ];

  private readonly keyMap: Record<string, string> = {
    '红色': 'R', '蓝色': 'B', '绿色': 'G', '紫色': 'P', '青色': 'C',
    '←': 'ArrowLeft', '→': 'ArrowRight', '↑': 'ArrowUp', '↓': 'ArrowDown',
    '1': '1', '2': '2', '3': '3', '4': '4', '5': '5',
  };

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.graphics = scene.add.graphics().setDepth(20);
  }

  start() {
    this.spawnTimer = this.scene.time.addEvent({
      delay: this.spawnInterval,
      callback: () => this.spawnChallenge(),
      loop: true,
    });

    // First challenge after 2 seconds
    this.scene.time.delayedCall(2000, () => this.spawnChallenge());
  }

  setDifficulty(scale: number) {
    this.difficultyScale = scale;
    this.spawnInterval = Math.max(2000, 5000 / scale);
    if (this.spawnTimer) {
      this.spawnTimer.remove();
      this.spawnTimer = this.scene.time.addEvent({
        delay: this.spawnInterval,
        callback: () => this.spawnChallenge(),
        loop: true,
      });
    }
  }

  setMaxActive(max: number) {
    this.maxActive = max;
  }

  private spawnChallenge() {
    if (this.activeChallenges.length >= this.maxActive) return;

    const cam = this.scene.cameras.main;
    const types: ChallengeType[] = ['color', 'arrow', 'number'];
    const type = types[Math.floor(Math.random() * types.length)];

    // Position along edges
    const side = Math.floor(Math.random() * 4);
    let x: number, y: number;
    switch (side) {
      case 0: x = Math.random() * cam.width; y = 40; break;
      case 1: x = cam.width - 40; y = Math.random() * cam.height; break;
      case 2: x = Math.random() * cam.width; y = cam.height - 40; break;
      default: x = 40; y = Math.random() * cam.height; break;
    }

    // Clamp to visible area
    x = Phaser.Math.Clamp(x, 80, cam.width - 80);
    y = Phaser.Math.Clamp(y, 80, cam.height - 80);

    let challenge: ChallengeEvent;
    switch (type) {
      case 'color':
        challenge = this.createColorChallenge(x, y);
        break;
      case 'arrow':
        challenge = this.createArrowChallenge(x, y);
        break;
      case 'number':
        challenge = this.createNumberChallenge(x, y);
        break;
    }

    this.showChallenge(challenge!);
  }

  private createColorChallenge(x: number, y: number): ChallengeEvent {
    const c = this.colorChallenges[Math.floor(Math.random() * this.colorChallenges.length)];
    return {
      type: 'color',
      value: c.value,
      x,
      y,
      duration: Math.max(2000, 4000 / this.difficultyScale),
      createdAt: this.scene.time.now,
    };
  }

  private createArrowChallenge(x: number, y: number): ChallengeEvent {
    const a = this.arrowChallenges[Math.floor(Math.random() * this.arrowChallenges.length)];
    return {
      type: 'arrow',
      value: a.value,
      x,
      y,
      duration: Math.max(2500, 5000 / this.difficultyScale),
      createdAt: this.scene.time.now,
    };
  }

  private createNumberChallenge(x: number, y: number): ChallengeEvent {
    const n = this.numberChallenges[Math.floor(Math.random() * this.numberChallenges.length)];
    return {
      type: 'number',
      value: n.value,
      x,
      y,
      duration: Math.max(2000, 4000 / this.difficultyScale),
      createdAt: this.scene.time.now,
    };
  }

  private showChallenge(data: ChallengeEvent) {
    this.challengeCount++;

    const bgColor = data.type === 'color'
      ? this.colorChallenges.find((c) => c.value === data.value)?.color ?? COLORS.cyan
      : data.type === 'number' ? COLORS.cyan : COLORS.darkGray;

    const displayText = this.getDisplayText(data);

    const displayColor = data.type === 'color'
      ? this.colorChallenges.find((c) => c.value === data.value)?.displayColor ?? '#00f0ff'
      : data.type === 'arrow'
      ? this.arrowChallenges.find((a) => a.value === data.value)?.displayColor ?? '#ffea00'
      : '#00f0ff';

    const bg = this.scene.add.rectangle(0, 0, 120, 50, bgColor, 0.3).setStrokeStyle(2, bgColor);
    const text = this.scene.add.text(0, 0, displayText, {
      fontFamily: 'Arial, sans-serif',
      fontSize: data.type === 'arrow' ? '28px' : '18px',
      color: displayColor,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const instruction = this.scene.add.text(0, 22, `按 ${this.getKeyLabel(data)}`, {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '10px',
      color: '#aaaaaa',
    }).setOrigin(0.5);

    const container = this.scene.add.container(data.x, data.y, [bg, text, instruction]).setDepth(20);
    container.setAlpha(0);

    // Fade in
    this.scene.tweens.add({
      targets: container,
      alpha: 1,
      duration: 200,
    });

    // Pulse effect
    this.scene.tweens.add({
      targets: container,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 600,
      yoyo: true,
      repeat: -1,
    });

    // Timer
    const timer = this.scene.time.delayedCall(data.duration, () => {
      this.failChallenge(container, data);
    });

    const active: ActiveChallenge = {
      data,
      container,
      timer,
      bg,
      text,
      highlight: instruction,
    };
    this.activeChallenges.push(active);
  }

  private getDisplayText(data: ChallengeEvent): string {
    switch (data.type) {
      case 'color': return data.value;
      case 'arrow': return data.value;
      case 'number': return `数字 ${data.value}`;
      default: return data.value;
    }
  }

  private getDisplayColor(data: ChallengeEvent): string {
    if (data.type === 'color') {
      return this.colorChallenges.find((c) => c.value === data.value)?.displayColor ?? '#00f0ff';
    }
    if (data.type === 'arrow') {
      return this.arrowChallenges.find((a) => a.value === data.value)?.displayColor ?? '#ffea00';
    }
    return this.numberChallenges.find((n) => n.value === data.value)?.displayColor ?? '#00f0ff';
  }

  private getKeyLabel(data: ChallengeEvent): string {
    const key = this.getChallengeKey(data);
    if (data.type === 'arrow') {
      return data.value;
    }
    if (data.type === 'color') {
      return `键 ${key}`;
    }
    return `键 ${key}`;
  }

  handleKeyInput(keyCode: string): boolean {
    for (let i = this.activeChallenges.length - 1; i >= 0; i--) {
      const ac = this.activeChallenges[i];
      const expectedKey = this.keyMap[ac.data.value] ?? this.getChallengeKey(ac.data);

      if (keyCode === expectedKey || keyCode === `Key${expectedKey}`) {
        this.succeedChallenge(ac);
        return true;
      }
    }
    return false;
  }

  private getChallengeKey(data: ChallengeEvent): string {
    if (data.type === 'color') {
      return this.colorChallenges.find((c) => c.value === data.value)?.key ?? '?';
    }
    if (data.type === 'arrow') {
      return this.arrowChallenges.find((a) => a.value === data.value)?.key ?? data.value;
    }
    return this.numberChallenges.find((n) => n.value === data.value)?.key ?? data.value;
  }

  private succeedChallenge(ac: ActiveChallenge) {
    ac.timer.remove();
    this.successCount++;

    // Success flash
    this.scene.tweens.add({
      targets: ac.container,
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0,
      duration: 300,
      onComplete: () => ac.container.destroy(),
    });

    // Green flash
    const flash = this.scene.add.graphics().setDepth(21);
    flash.fillStyle(COLORS.green, 0.3);
    flash.fillCircle(ac.container.x, ac.container.y, 40);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 400,
      onComplete: () => flash.destroy(),
    });

    this.scene.events.emit('challenge-success', ac.data);
    this.activeChallenges.splice(this.activeChallenges.indexOf(ac), 1);
  }

  private failChallenge(container: Phaser.GameObjects.Container, data: ChallengeEvent) {
    this.failCount++;

    // Fail flash
    this.scene.tweens.add({
      targets: container,
      alpha: 0,
      duration: 500,
      onComplete: () => container.destroy(),
    });

    // Red flash
    const flash = this.scene.add.graphics().setDepth(21);
    flash.fillStyle(COLORS.red, 0.3);
    flash.fillCircle(container.x, container.y, 40);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 500,
      onComplete: () => flash.destroy(),
    });

    this.scene.events.emit('challenge-fail', data);

    const idx = this.activeChallenges.findIndex((ac) => ac.data === data);
    if (idx >= 0) this.activeChallenges.splice(idx, 1);
  }

  getCompletionRate(): number {
    if (this.challengeCount === 0) return 0;
    return this.successCount / this.challengeCount;
  }

  getStats() {
    return {
      total: this.challengeCount,
      success: this.successCount,
      fail: this.failCount,
      rate: this.getCompletionRate(),
    };
  }

  stop() {
    if (this.spawnTimer) {
      this.spawnTimer.remove();
      this.spawnTimer = null;
    }
    this.activeChallenges.forEach((ac) => {
      ac.timer.remove();
      ac.container.destroy();
    });
    this.activeChallenges = [];
  }

  destroy() {
    this.stop();
    this.graphics.destroy();
  }
}
